import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0AvatarList, F0Dialog, PersonAvatarVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  CommunityMemberEmployeeFragment,
  GroupMembersChangedDocument,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import 'modules/meetings/lib/utils/index.styles.css'
import AutomaticInvitesView from 'modules/posts/components/Groups/CommunityHeader/AutomaticInvitesView'
import InviteMembersView from 'modules/posts/components/Groups/CommunityHeader/InviteMembersView'
import { useMembersListModal } from 'modules/posts/components/Groups/CommunityHeader/useMembersListModal'
import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'

type View = 'members' | 'invite' | 'automatic-invites'

type Props = {
  groupId: string
  groupTitle: string
  initialAvatars: PersonAvatarVariant[]
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  showTrigger?: boolean
  canManageMembers?: boolean
  isDynamicCommunitiesEnabled?: boolean
  dynamicTeamId?: string | null
  initialView?: View
}

type GroupEmployee = CommunityMemberEmployeeFragment

export const MembersList: FC<Props> = ({
  groupId,
  groupTitle,
  initialAvatars,
  isOpen: controlledIsOpen,
  onOpenChange,
  showTrigger = true,
  canManageMembers = false,
  isDynamicCommunitiesEnabled = false,
  dynamicTeamId = null,
  initialView = 'members',
}) => {
  const initialTotalCount = initialAvatars.length
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  // Track selected items from OneDataCollection
  const [isRemoving, setIsRemoving] = useState(false)
  const clearSelectedItemsRef = useRef<(() => void) | null>(null)

  const isOpen = controlledIsOpen ?? internalIsOpen
  const setIsOpen = useCallback(
    (value: boolean) => {
      if (controlledIsOpen === undefined) {
        setInternalIsOpen(value)
      }
      onOpenChange?.(value)
    },
    [controlledIsOpen, onOpenChange]
  )

  const {
    view,
    setView,
    dataSource,
    handleBulkAction,
    employeeSelection,
    handleInviteEmployeeChange,
    handleInviteMembersSubmit,
    isInvitingMembers,
    invitedMembersCount,
    setInvitedMembersCount,
    handleAutoInvitesSpecificationLoaded,
    handleAutoInvitesSpecificationChange,
    handleAutoInvitesEvaluationError,
    handleAutoInvitesSubmit,
    isSubmittingAutoInvites,
    autoInvitesError,
    showFeedbackSuccess,
    setShowFeedbackSuccess,
    hasAutoInvitesChanges,
    hasEvaluationError,
    handleBackToMembers,
    refetchMembersList,
    setEmployeeSelection,
  } = useMembersListModal({
    groupId,
    groupTitle,
    canManageMembers,
    isDynamicCommunitiesEnabled,
    dynamicTeamId,
  })

  // Subscribe to group member changes for real-time updates
  useSubscription(GroupMembersChangedDocument, {
    variables: { id: groupId.toString() },
    skip: !isOpen,
    onData: ({ data }) => {
      const updatedGroupId = data?.data?.postsGroupUpdate?.group?.id
      if (updatedGroupId && updatedGroupId === groupId) {
        refetchMembersList()
      }
    },
  })

  const handleCloseModal = useCallback(() => {
    setIsOpen(false)
    handleBackToMembers()
    setShowFeedbackSuccess(false)
    setInvitedMembersCount(null)

    // Clear selections when closing
    clearSelectedItemsRef.current?.()
  }, [setIsOpen, handleBackToMembers, setShowFeedbackSuccess, setInvitedMembersCount])

  // Handle removing selected members
  const handleRemoveSelectedMembers = useCallback(async () => {
    if (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0) return

    setIsRemoving(true)
    setShowFeedbackSuccess(false)
    setInvitedMembersCount(null)
    await handleBulkAction('remove')
    setIsRemoving(false)

    // Clear selection after successful removal
    clearSelectedItemsRef.current?.()
  }, [employeeSelection, handleBulkAction, setShowFeedbackSuccess, setInvitedMembersCount])

  // Handle clearing selection
  const handleClearSelection = useCallback(() => {
    clearSelectedItemsRef.current?.()
  }, [])

  const MAX_VISIBLE_AVATARS = 3
  const visibleAvatars = initialAvatars.slice(0, MAX_VISIBLE_AVATARS)
  const remainingCount =
    initialTotalCount > MAX_VISIBLE_AVATARS ? initialTotalCount - MAX_VISIBLE_AVATARS : 0

  const modalTitle = useMemo(() => {
    switch (view) {
      case 'invite':
        return i18n.t('dashboard.groups.add_members.title')
      case 'automatic-invites':
        return i18n.t('posts.communities.automatic_invites.title')
      default:
        return i18n.t('posts.communities.members_title', {
          membersCount: initialTotalCount,
          groupTitle,
        })
    }
  }, [view, initialTotalCount, groupTitle])

  const modalDescription = useMemo(() => {
    switch (view) {
      case 'invite':
        return i18n.t('dashboard.groups.add_members.description')
      case 'automatic-invites':
        return i18n.t('posts.communities.automatic_invites.description')
      default:
        return undefined
    }
  }, [view])

  // Open to a specific view when modal opens (for external control)
  useEffect(() => {
    if (isOpen) {
      // Only set the initial view when modal first opens
      setView(initialView)
      setShowFeedbackSuccess(false)
      setInvitedMembersCount(null)
    } else {
      // Reset to members view when modal closes
      setView('members')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <>
      {showTrigger && (
        <Box cursor="pointer" onClick={() => setIsOpen(true)} width="fit-content">
          <F0AvatarList
            avatars={visibleAvatars}
            remainingCount={remainingCount}
            size="sm"
            type="person"
          />
        </Box>
      )}
      <F0Dialog
        isOpen={isOpen}
        onClose={handleCloseModal}
        title={modalTitle}
        description={modalDescription}
        width="lg"
        primaryAction={
          view === 'invite'
            ? {
                label: i18n.t('dashboard.groups.add_members.submit'),
                onClick: handleInviteMembersSubmit,
                disabled: employeeSelection.type === 'manual' && employeeSelection.ids.length === 0,
                loading: isInvitingMembers,
              }
            : view === 'automatic-invites'
              ? {
                  label: isSubmittingAutoInvites ? i18n.t('common.saving') : i18n.t('common.save'),
                  onClick: handleAutoInvitesSubmit,
                  disabled: isSubmittingAutoInvites || !hasAutoInvitesChanges || hasEvaluationError,
                  loading: isSubmittingAutoInvites,
                }
              : view === 'members' && canManageMembers
                ? {
                    label: i18n.t('posts.communities.remove_members'),
                    onClick: handleRemoveSelectedMembers,
                    disabled:
                      isRemoving ||
                      (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0),
                    loading: isRemoving,
                  }
                : undefined
        }
        secondaryAction={
          view === 'invite' || view === 'automatic-invites'
            ? {
                label: i18n.t('common.cancel'),
                onClick: handleBackToMembers,
                disabled: isInvitingMembers || isSubmittingAutoInvites,
              }
            : view === 'members' && canManageMembers
              ? {
                  label: i18n.t('common.clear_selection'),
                  onClick: handleClearSelection,
                  disabled:
                    isRemoving ||
                    (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0),
                }
              : undefined
        }
      >
        {isOpen && (
          <>
            {view === 'members' && (
              <Box
                style={{ maxHeight: '400px', minHeight: '400px' }}
                className={'utils_hiddenScrollBar'}
              >
                {showFeedbackSuccess && (
                  <Box style={{ marginBottom: '16px' }}>
                    <F0Alert
                      variant="info"
                      title={
                        invitedMembersCount !== null
                          ? i18n.tp('posts.communities.members_invited_success_info', {
                              count: invitedMembersCount,
                            })
                          : i18n.t('posts.communities.automatic_invites.success_info')
                      }
                      description=""
                    />
                  </Box>
                )}
                <OneDataCollection
                  id={`community-members-list-${groupId}/v1`}
                  source={dataSource}
                  fullHeight
                  emptyStates={{
                    'no-data': {
                      title: i18n.t('posts.communities.no_result'),
                    },
                  }}
                  onSelectItems={(selectedItems, clearSelectedItems) => {
                    clearSelectedItemsRef.current = clearSelectedItems

                    const allSelected =
                      selectedItems.allSelected === true ||
                      selectedItems.allSelected === 'indeterminate'

                    const excludedIds =
                      selectedItems.allSelected === 'indeterminate'
                        ? selectedItems.itemsStatus
                            ?.filter((item) => !item.checked)
                            .map((item) => String(item.item.id)) || []
                        : []

                    const selectedIds =
                      selectedItems.allSelected === false
                        ? [...(selectedItems.selectedIds || [])].map(String)
                        : []

                    const newSelection: EmployeeSelectorV2MultiSelectState = allSelected
                      ? {
                          type: 'all',
                          excludedIds,
                          filters: {}, // No filters support for now
                        }
                      : {
                          type: 'manual',
                          ids: selectedIds,
                        }

                    setEmployeeSelection(newSelection)
                  }}
                  visualizations={[
                    {
                      type: 'table',
                      options: {
                        columns: [
                          {
                            label: i18n.t('posts.communities.members_name'),
                            render: (item: GroupEmployee) => {
                              const avatarUrl = buildAvatarUrl(item.avatar)
                              return {
                                type: 'person',
                                value: {
                                  firstName: item.firstName ?? '',
                                  lastName: item.lastName ?? '',
                                  src: avatarUrl,
                                },
                              }
                            },
                          },
                          {
                            label: i18n.t('posts.communities.members_role'),
                            render: (item: GroupEmployee) => {
                              const role =
                                item?.referenceContract?.jobCatalogTreeLevel?.ancestor?.name ??
                                item?.referenceContract?.jobCatalogLevel?.roleName ??
                                item?.referenceContract?.jobTitle ??
                                ''
                              return {
                                type: 'text',
                                value: role,
                              }
                            },
                          },
                        ],
                      },
                    },
                  ]}
                />
              </Box>
            )}
            {view === 'invite' && (
              <InviteMembersView
                groupId={groupId}
                onChange={handleInviteEmployeeChange}
                disabled={isInvitingMembers}
              />
            )}
            {view === 'automatic-invites' && (
              <Box style={{ minHeight: '400px' }}>
                <AutomaticInvitesView
                  dynamicTeamId={dynamicTeamId}
                  onSpecificationLoaded={handleAutoInvitesSpecificationLoaded}
                  onSpecificationChange={handleAutoInvitesSpecificationChange}
                  onEvaluationError={handleAutoInvitesEvaluationError}
                  disabled={isSubmittingAutoInvites}
                  error={autoInvitesError}
                />
              </Box>
            )}
          </>
        )}
      </F0Dialog>
    </>
  )
}
